/*
 * File: StoryCard.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 5, 2025 at 8:51 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	memo,
	useContext, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

// Utilities
import { formatNumber } from '../../../utilities/utilities';
import { generateShortFormContentPlaceholder, generateShortTitlePlaceholder } from '../../utilities/placeholder';

// Services
import { recordReaction, removeReaction } from '../../services/reaction';

// Store
import { updateUser } from '../../store/slices/user/user.slice';
import { toggleReactionAnimation } from '../../store/slices/animation/animation.slice';

// Context
import ActionContext from '../../context/SharedActionContext';
import AnimationContextBuilder from '../../context/AnimationContext';

// Components
import { Typography } from '../Typography';
import { ContentLocalityBadge } from '../ContentLocalityBadge';
import { ObfuscatedText } from '../ObfuscatedText';
import { LocaleLink } from '../LocaleLink';
import { Badge } from '../Badge';
import { ToolTip } from '../ToolTip';
import { ActionModule } from '../ActionModule';
import { ClampContent } from '../ClampContent';
import { Button } from '../Button';
import { CardOptionsWidget } from '../CardOptionsWidget';
import { FlyToTargetContext } from '../FlyToTargetContext';

// Constants
import { AVAILABLE_I18N_NAMESPACES, REACTIONS, SOURCES } from '../../../constants/base.constants';

// Styles
import * as S from './StoryCard.styles';


/**
 * Component
 */

export const StoryCard = memo(({
	className, story, source, isPlaceholder, context, index
}) => {

	// Get context from navigation parent
	const {
		dispatch: actionDispatch
	} = useContext(ActionContext);

	// Get context from navigation parent
	const {
		state: animationState
	} = useContext(AnimationContextBuilder);

	// Get current user from hook
	const currentUser = useSelector((state) => state.user.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Generate placeholder
	const titlePlaceholder = useMemo(() => generateShortTitlePlaceholder(index), [index]);
	const excerptPlaceholder = useMemo(() => generateShortFormContentPlaceholder(index), [index]);

	// Get reaction
	const hasReaction = useMemo(() => {

		// Get reaction schema
		const reactionSchema = currentUser.reaction_schema || {};

		// Check for active reaction
		return reactionSchema[story.story_id] && reactionSchema[story.story_id].includes(REACTIONS.reaction_a.code);

	}, [currentUser, story]);

	// Get unseen updates
	const unseenUpdates = useMemo(() => {

		// Get updates schema
		const updatesSchema = currentUser.update_schema || {};

		// Check for active reaction
		const seenUpdates = updatesSchema[story.story_id] || [];

		// Return unseen updates
		return (story.updates || []).filter(({ update_id: updateId }) => !seenUpdates.includes(updateId)).map((u) => u.update_id);

	}, [currentUser, story]);

	// Create state
	const [isReactionActive, setIsReactionActive] = useState(hasReaction === true);
	const [isMediaObfuscated, setIsMediaObfuscated] = useState(story.is_triggering);
	const [animationDuration, setAnimationDuration] = useState(2.6);

	// Create refs for elements
	const reactionActionModuleRef = useRef();

	// Use hooks
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Build title object
	const title = useMemo(() => (story && story.story_id ? `${!story?.title ? `#${story?.number}` : story.title}` : ''), [story]);

	// Handle uncensor image
	const uncensorImage = (e) => {

		// Prevent default
		e.preventDefault();

		// Update state
		setIsMediaObfuscated(false);
	};

	// Handle report story
	const reportStory = () => {

		// Open report modal
		actionDispatch({
			type: 'OPEN_REPORT_MODAL',
			payload: {
				source: SOURCES.STORY,
				sourceId: story.story_id
			}
		});
	};

	// Handle story reaction
	const handleReaction = async () => {

		// Handle action for state
		if (!isReactionActive) {

			// Update state
			setIsReactionActive(true);

			// Trigger reaction animation
			setTimeout(() => {
				dispatch(toggleReactionAnimation(true));
				setTimeout(() => {
					dispatch(toggleReactionAnimation(false));
				}, 800);
			}, animationDuration * 1000);

			// Record reaction
			const { data: { result: { user } } } = await recordReaction({
				source,
				entityType: SOURCES.STORY,
				entityId: story.story_id,
				code: REACTIONS.reaction_a.code,
				urlPath: window.location.pathname
			});

			// Update user
			if (user) {
				dispatch(updateUser(user));
			}

		} else {

			// Update state
			setIsReactionActive(false);

			// Remove reaction
			const { data: { result: { user } } } = await removeReaction({
				source,
				entityType: SOURCES.STORY,
				entityId: story.story_id,
				code: REACTIONS.reaction_a.code,
				urlPath: window.location.pathname
			});

			// Update user
			if (user) {
				dispatch(updateUser(user));
			}
		}
	};

	// Handle actions on reaction state change
	useEffect(() => {

		// Update reaction active
		setIsReactionActive(hasReaction === true);

	}, [hasReaction]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<S.Wrapper className={className} $isPlaceholder={isPlaceholder}>

			{/* Header */}
			<S.CardHeader>

				{/* Prompt Container */}
				<S.PromptContainer>

					{/* Prompt */}
					<Typography tag="h5" weight="bold" className={isPlaceholder && 'shimmer shimmer-element'}>
						{context === 'all' ? (
							<Trans t={t} i18nKey="storyCard.story">
								Story
							</Trans>
						) : (
							<Trans t={t} i18nKey="storyCard.survivorStory">
								Survivor story
							</Trans>
						)}
					</Typography>

					{/* Survivor Badge */}
					{!isPlaceholder && context === 'all' && (
						<S.SurvivorBadge>
							<Typography tag="span" variation="3" weight="semibold">
								<Trans t={t} i18nKey="survivorAttribution">
									From a survivor
								</Trans>
							</Typography>
						</S.SurvivorBadge>
					)}

				</S.PromptContainer>

				{/* Content Locality Badge */}
				{!isPlaceholder && <ContentLocalityBadge country={story.submitted_country} id={story.story_id} />}

			</S.CardHeader>

			{/* Badges */}
			<S.BadgeContainer>

				{/* Partner Badge */}
				{story?.partner && partner?.id && story?.partner.id !== partner?.id && (
					<LocaleLink to={`https://${story?.partner.full_domain}`} target="_blank">
						<Badge type="neutral" variation="square" size="small" className="rightIcon">
							<Typography weight="medium">
								<Trans t={t} i18nKey="communityBadge">
									{{ partnerName: story?.partner.name }}
									{' '}
									Community
								</Trans>
							</Typography>
							<FontAwesomeIcon icon={['fas', 'arrow-up-right']} />
						</Badge>
					</LocaleLink>
				)}

				{/* Updates Badge */}
				{unseenUpdates.length > 0 && (
					<LocaleLink to={`/story/${story.link_component}#updates`}>
						<Badge type="neutral" variation="square" size="small" className="leftIcon">
							<FontAwesomeIcon icon={['far', 'clock']} />
							<Typography weight="medium">
								{unseenUpdates.length === 1
									? (
										<Trans t={t} i18nKey="storyCard.newUpdateSingle">
											{{ updateCount: unseenUpdates.length }}
											{' '}
											new update
										</Trans>
									) : (
										<Trans t={t} i18nKey="storyCard.newUpdateMultiple">
											{{ updateCount: unseenUpdates.length }}
											{' '}
											new updates
										</Trans>
									)}
							</Typography>
						</Badge>
					</LocaleLink>
				)}

			</S.BadgeContainer>

			{/* Content Link */}
			<LocaleLink to={`/story/${story.link_component}`}>

				{/* Story Container */}
				<S.StoryContainer>

					{/* Title */}
					<ClampContent clamp={6}>
						<Typography
							tag="h4"
							weight="bold"
							style={{ margin: '0px 0px 8px' }}
							className={isPlaceholder && 'shimmer shimmer-element'}
						>
							<ObfuscatedText>{title || titlePlaceholder}</ObfuscatedText>
						</Typography>
					</ClampContent>

					{/* Content */}
					{!story.media ? (
						<ClampContent clamp={12}>
							<Typography tag="p" variation="2" className={isPlaceholder && 'shimmer shimmer-element'}>
								<ObfuscatedText>
									{story.content || excerptPlaceholder}
								</ObfuscatedText>
							</Typography>
						</ClampContent>
					) : (
						<S.MediaContainer className={isMediaObfuscated && 'trigger'}>
							{isMediaObfuscated && (
								<S.TriggerToggleContainer>
									<S.LightenOverlay />
									<Button variation="white" onClick={uncensorImage}>
										<Trans t={t} i18nKey="storyCard.showArtworkAction">
											Show Artwork
										</Trans>
									</Button>
								</S.TriggerToggleContainer>
							)}
							<S.Overlay />
							<S.MediaImage src={story.media} title={title} alt={title} />
						</S.MediaContainer>
					)}

				</S.StoryContainer>
			</LocaleLink>

			{/* Trigger Warning */}
			{story.is_triggering && story.triggers.length > 0 && (
				<S.TriggerWarning>
					<Typography tag="p" variation="3">
						<Trans t={t} i18nKey={`triggerWarnings.${story.triggers[0]}_story`}>
							Dear reader, the following story contains language of self-harm that some may find triggering or discomforting.
						</Trans>
					</Typography>
				</S.TriggerWarning>
			)}

			{/* Action Bar */}
			{!isPlaceholder && (
				<S.ActionBar>

					{/* Action - React */}
					<FlyToTargetContext
						targets={{ desktop: 'desktopReactionMetric', mobile: 'mobileReactionMetric' }}
						flyElement={<FontAwesomeIcon className="love active" icon={['fas', 'heart']} />}
						navigationState={animationState}
						ref={reactionActionModuleRef}
						refOffsetTop={-5}
						setAnimationDuration={setAnimationDuration}
					>
						<ActionModule
							onClick={handleReaction}
							canFly={!isReactionActive}
							ref={reactionActionModuleRef}
							data-tip={t('reactions.reaction_a')}
							data-for={`${story.story_id}-heart`}
						>
							<FontAwesomeIcon className={isReactionActive ? 'love active' : 'love'} icon={isReactionActive ? ['fas', 'heart'] : ['far', 'heart']} />
							{isReactionActive && (
								<Typography tag="span">
									<Trans t={t} i18nKey="youAndOthers">
										<Typography className="fontBold">you</Typography>
										{' '}
										<Typography className="fontRegular">and</Typography>
										{' '}
										<Typography className="fontBold">others</Typography>
									</Trans>
								</Typography>
							)}
						</ActionModule>
					</FlyToTargetContext>

					{/* Action - Comment */}
					{story.comments_allowed && (
						<ActionModule
							onClick={() => { navigate(`${locale.localePath}/story/${story.link_component}#comments`); }}
						>
							<FontAwesomeIcon className="comment" icon={['far', 'comment']} />
							<Typography tag="span">{formatNumber(story.comment_count) || '–'}</Typography>
						</ActionModule>
					)}

					{/* Tooltip */}
					<ToolTip id={`${story.story_id}-heart`} weight="medium" />

					{/* Spacer */}
					<S.ActionSpacer />

					{/* Options Dropdown */}
					<CardOptionsWidget items={[
						{
							icon: ['far', 'flag'],
							text: t('actions.report'),
							action: reportStory
						}]}
					/>

				</S.ActionBar>
			)}
		</S.Wrapper>
	);
});


/**
 * Configuration
 */

StoryCard.displayName = 'StoryCard';
StoryCard.propTypes = {
	className: PropTypes.string,
	story: PropTypes.shape(),
	source: PropTypes.oneOf(Object.values(SOURCES)),
	context: PropTypes.oneOf(['all', 'stories']),
	isPlaceholder: PropTypes.bool,
	index: PropTypes.number
};
StoryCard.defaultProps = {
	className: null,
	story: {},
	source: null,
	context: 'all',
	isPlaceholder: false,
	index: 0
};
